import { assert, logger } from '@fe/core';
import { AxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { Evented } from 'maplibre-gl';
import { localStorageService } from '../../client/local-storage-service';
import { LoginIdentityProviderResultCode, LoginResultCode } from '../../common/enums';
import {
    AuthService,
    LoginErrorResponseModel,
    LoginResponseModelV2,
    LoginSsoErrorResponseModel,
    LoginSsoResponseModel,
    RefreshTokenResponseModel
} from '../axios/axios-api-v2-generated';

export interface AccountInfo {
    userId: string;
    accessToken: string;
    accessTokenPayload: TokenPayload;
    refreshToken: string;
    dmGroupId: string;
    fpmUserId: string | null;
}

interface TokenPayload {
    exp: number;
    sub: string;
    DMGroupID: string;
}

type AuthInstanceEvents = {
    'account-changed': { type: 'account-changed' };
    'login-success': { type: 'login-success'; account: AccountInfo };
    'logout-success': { type: 'logout-success' };
    initialized: { type: 'initialized' };
};

export class AuthInstance extends Evented<AuthInstanceEvents> {
    private isInitialized = false;
    private autoLogin = false;
    private account: AccountInfo | null = null;
    private autoLoginPassword: string | null = null;

    getIsInitialized() {
        return this.isInitialized;
    }

    getAccount() {
        return this.account;
    }

    setAutoLogin(enabled: boolean, password: string | null) {
        this.autoLogin = enabled;
        this.autoLoginPassword = password;
    }

    initialize(): Promise<void> {
        return navigator.locks.request('auth-init', async () => {
            if (this.isInitialized) {
                return;
            }

            if (!(await this.tryLoginSilent())) {
                this.reset();
            }

            this.onInitialized();
        });
    }

    async loginWithCredentials(
        username: string,
        password: string
    ): Promise<
        | {
              ok: false;
              code: LoginResultCode;
          }
        | { ok: true }
    > {
        assert(!this.account);
        let response: LoginResponseModelV2 | undefined;
        try {
            response = await AuthService.loginAsync(
                {
                    body: {
                        userId: username,
                        password: password
                    }
                },
                { withAuthorization: false }
            );
        } catch (err) {
            if (err instanceof AxiosError) {
                if (err.status === 400 && err.response?.data) {
                    return { ok: false, code: (err.response.data as LoginErrorResponseModel).code! };
                }
            }
            return { ok: false, code: LoginResultCode.serviceUnavailable };
        }

        this.isInitialized = true;
        this.storeTokenResponse(response);
        localStorageService.setWsPasscodeFromPassword(password);

        this.fire('login-success', { account: this.account });
        this.fire('account-changed');
        return { ok: true };
    }

    async acquireToken(): Promise<string> {
        return await window.navigator.locks.request('token', async () => {
            return await this.acquireTokenInternal();
        });
    }

    private async acquireTokenInternal(): Promise<string> {
        if (!this.account) {
            return '';
        }

        if (!this.isTokenExpired(this.account.accessTokenPayload)) {
            return this.account.accessToken;
        }

        logger.info('Refreshing access token');

        // refresh
        let response: LoginResponseModelV2 | undefined;
        try {
            response = await AuthService.refreshTokenAsync(
                { body: { refreshToken: this.account.refreshToken } },
                { withAuthorization: false }
            );
        } catch (err) {
            logger.trackException(err);
            logger.info('Cannot refresh the token', err);
            if (err instanceof AxiosError) {
                // Only bad request is definitive fail to refresh
                // Everything else shouldn't trigger a logout
                if (err.response?.status === 400) {
                    this.logoutSilent();
                }
            }
            return '';
        }

        this.storeTokenResponse(response);
        this.fire('account-changed');
        return this.account.accessToken;
    }

    async logoutSilent() {
        this.reset();
        this.fire('account-changed');
        this.fire('logout-success');
    }

    async logout(): Promise<void> {
        assert(this.account);
        try {
            await AuthService.logoutAsync({ body: { refreshToken: this.account.refreshToken } });
        } catch (err) {
            logger.error(err);
        }
        this.reset();
    }

    reset() {
        logger.info('Resetting auth service');
        this.account = null;
        localStorageService.remove('StorageUserModelKey');
        localStorageService.remove('RefreshTokenKey');
        localStorageService.remove('StorageUsernameKey');
        localStorageService.removeWsPasscode();
        this.fire('account-changed');
    }

    async completeSooLogin(): Promise<LoginIdentityProviderResultCode> {
        try {
            let response: LoginSsoResponseModel | null = null;
            try {
                response = await AuthService.loginSsoComplete({ withAuthorization: false, withCredentials: true });
            } catch (err) {
                if (err instanceof AxiosError) {
                    if (err.status === 400 && err.response?.data) {
                        const response = err.response.data as LoginSsoErrorResponseModel;
                        return response.code!;
                    } else if (err.status === 401 || err.status === 404) {
                        // Somebody called with non existing session data
                        return LoginIdentityProviderResultCode.unknownError;
                    } else {
                        return LoginIdentityProviderResultCode.serviceUnavailable;
                    }
                }
            }

            if (response) {
                this.storeTokenResponse(response);
                // If we have fpm id store it
                if (response.fpmId) {
                    this.account!.fpmUserId = response.fpmId ?? null;
                }
                localStorageService.setWsPasscodeRaw(response.wsPasscode!);

                this.fire('account-changed');
                this.fire('login-success', { account: this.account });
            }
            return LoginIdentityProviderResultCode.ok;
        } catch (err) {
            logger.trackException(err);
        }

        return LoginIdentityProviderResultCode.serviceUnavailable;
    }

    /**
     * Attempts to start the session with stored credentials
     * @returns True if successful otherwise false
     */
    private async tryLoginSilent(): Promise<boolean> {
        try {
            const userId = localStorageService.get('StorageUsernameKey');
            const fesPassword = localStorageService.getWsPasscode();
            const refreshToken = localStorageService.get('RefreshTokenKey');

            if (!userId) {
                logger.info(`Can't retrieve userid from localStorage`);
                // Can't do much in that case
                return false;
            }

            if (!fesPassword) {
                logger.info(`Can't retrieve ws passcode from localStorage`);
                // Can't do much in that case either
                return false;
            }

            if (!refreshToken && !this.autoLogin) {
                logger.info(`No refresh token present and auto login disabled`);
                // If we don't have the refresh token and auto login is disabled, we can't do anything
                return false;
            }

            let response: LoginResponseModelV2 | undefined;
            // If we have a refresh token, try it
            if (refreshToken) {
                try {
                    response = await AuthService.refreshTokenAsync({ body: { refreshToken } }, { withAuthorization: false });
                } catch {
                    // Will handle below
                }
            }

            // If we didn't succeed with the refresh token
            if (!response) {
                // Check if we can auto login
                if (this.autoLogin) {
                    if (!this.autoLoginPassword) {
                        logger.warn(`Auto login is enabled but no credentials were found in the storage.`);
                        return false;
                    }

                    try {
                        response = await AuthService.loginAsync(
                            { body: { userId, password: atob(this.autoLoginPassword) } },
                            { withAuthorization: false }
                        );
                    } catch {
                        // Will handle below
                    }
                }
            }

            // Didn't get a response
            if (!response) {
                return false;
            }

            this.storeTokenResponse(response);
            this.fire('login-success', { account: this.account });

            return true;
        } catch (err) {
            logger.trackException(err);
        }

        return false;
    }

    /**
     * Stores information from access token. This can be called during login, session refresh or sso completion
     * @param model
     */
    private storeTokenResponse(model: LoginResponseModelV2 | LoginSsoResponseModel | RefreshTokenResponseModel) {
        assert(model.refreshToken);
        assert(model.accessToken);

        const tokenPayload = jwtDecode<TokenPayload>(model.accessToken!);
        localStorageService.set('StorageUsernameKey', tokenPayload.sub);
        localStorageService.set('RefreshTokenKey', model.refreshToken);
        this.account = {
            userId: tokenPayload.sub,
            accessToken: model.accessToken,
            accessTokenPayload: tokenPayload,
            refreshToken: model.refreshToken,
            dmGroupId: tokenPayload.DMGroupID,
            // Copy fpm user id from the previous account object because it is only supplied in complete-sso call
            fpmUserId: this.account?.fpmUserId ?? null
        };
    }

    private isTokenExpired(token: TokenPayload) {
        const currentTime = Math.floor(Date.now() / 1000) + 15; // 15s buffer
        return currentTime >= token.exp;
    }

    private onInitialized() {
        this.isInitialized = true;
        this.fire('initialized');
    }
}

let instance: AuthInstance;

export function getAuthInstance() {
    return (instance ??= new AuthInstance());
}
